// OrganizationSettings.tsx
import React, { useState, useEffect } from 'react';
import { Building, Clock, Save, Sliders, GripVertical, ChevronDown, Trash2 } from 'lucide-react';
import SingleSelectLocalSearchDropdown from '../../components/SingleSelectLocalSearchDropdown';
import { showSuccess, showError } from '../../utils/toast';
import baseapi from '../../services/baseapi';
import { useClientLevel } from '../../hooks/useClientLevel';
import { jwt_decode } from '../../utils/jwt_decode';



// interface OrgSettings {
//     logo: string | null;
//     timezone: string;
//     columnOrder: string[];
//     defaultSortKey: string;
//     defaultSortDir: 'asc' | 'desc';
//     eventColumnOrder?: string[];
//     eventDefaultSortKey?: string;
//     eventDefaultSortDir?: 'asc' | 'desc';
//     assetColumnOrder?: string[];
//     assetDefaultSortKey?: string;
//     assetDefaultSortDir?: 'asc' | 'desc';
// }

const DEFAULT_COLUMNS = [
    { key: 'ip_address', label: 'IP Address' },
    { key: 'hostname', label: 'Hostname' },
    { key: 'agent_type', label: 'Agent Type' },
    { key: 'platform', label: 'Platform' },
    { key: 'version', label: 'Version' },
    { key: 'architecture', label: 'Architecture' },
    { key: 'status', label: 'Status' },
    { key: 'client_name', label: 'Client Name' },
    { key: 'last_seen', label: 'Updated' },
    { key: 'actions', label: 'Action' }
];

const EVENT_DEFAULT_COLUMNS = [
    { key: 'asset_hostname', label: 'Asset / Host' },
    { key: 'client_name', label: 'Client' },
    { key: 'event_type', label: 'Event Type' },
    { key: 'message', label: 'Message' },
    { key: 'event_status', label: 'Status' },
    { key: 'severity', label: 'Severity' },
    { key: 'ticket_number', label: 'Ticket' },
    { key: 'created', label: 'Created' },
    { key: 'actions', label: 'Actions' }
];

const ASSET_DEFAULT_COLUMNS = [
    { key: 'asset_name', label: 'Asset Name', sortKey: 'name' },
    { key: 'hostname', label: 'Hostname', sortKey: 'hostname' },
    { key: 'ip_address', label: 'IP Address', sortKey: 'ip_address' },
    { key: 'mac', label: 'MAC Address', sortKey: 'mac_address' },
    { key: 'os', label: 'Operating System', sortKey: 'os_name' },
    { key: 'manufacturer', label: 'Manufacturer', sortKey: 'manufacturer' },
    { key: 'type', label: 'Asset Type', sortKey: 'asset_type' },
    { key: 'status', label: 'Status', sortKey: 'status' },
    { key: 'tags', label: 'Tags', sortKey: 'tags' },
    { key: 'last_seen', label: 'Last Seen', sortKey: 'last_seen' },
    { key: 'client_name', label: 'Client Name', sortKey: 'client_name' },
    { key: 'protocols', label: 'Protocols', sortKey: 'protocols_performed' },
    { key: 'last_frame', label: 'Last Frame' },
    { key: 'actions', label: 'Actions' }
];

export default function OrganizationSettings() {
    const level = useClientLevel();
    const [logo, setLogo] = useState<string | null>(null);
    // const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [logoKey, setLogoKey] = useState<string>('');
    const [isLogoUploading, setIsLogoUploading] = useState(false);
    const [timezone, setTimezone] = useState<string>('UTC');
    const [columns, setColumns] = useState(DEFAULT_COLUMNS);
    const [defaultSortKey, setDefaultSortKey] = useState<string>('hostname');
    const [defaultSortDir, setDefaultSortDir] = useState<'asc' | 'desc'>('asc');
    const [isSaving, setIsSaving] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    // Event Table columns states
    const [eventColumns, setEventColumns] = useState(EVENT_DEFAULT_COLUMNS);
    const [eventDefaultSortKey, setEventDefaultSortKey] = useState<string>('created');
    const [eventDefaultSortDir, setEventDefaultSortDir] = useState<'asc' | 'desc'>('desc');
    const [draggedEventIndex, setDraggedEventIndex] = useState<number | null>(null);

    // Asset Table columns states
    const [assetColumns, setAssetColumns] = useState(ASSET_DEFAULT_COLUMNS);
    const [assetDefaultSortKey, setAssetDefaultSortKey] = useState<string>('last_seen');
    const [assetDefaultSortDir, setAssetDefaultSortDir] = useState<'asc' | 'desc'>('desc');
    const [draggedAssetIndex, setDraggedAssetIndex] = useState<number | null>(null);

    // Collapsible sections states
    const [isAgentOpen, setIsAgentOpen] = useState(false);
    const [isEventOpen, setIsEventOpen] = useState(false);
    const [isAssetOpen, setIsAssetOpen] = useState(false);

    const [deleteAssetsAfterDays, setDeleteAssetsAfterDays] = useState<number>(0);

    const timezoneList = Intl.supportedValuesOf('timeZone').map(tz => ({ label: tz, value: tz }));

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        setColumns(prev => {
            const updated = [...prev];
            const item = updated.splice(draggedIndex, 1)[0];
            updated.splice(index, 0, item);
            return updated;
        });
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleEventDragStart = (e: React.DragEvent, index: number) => {
        setDraggedEventIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleEventDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedEventIndex === null || draggedEventIndex === index) return;

        setEventColumns(prev => {
            const updated = [...prev];
            const item = updated.splice(draggedEventIndex, 1)[0];
            updated.splice(index, 0, item);
            return updated;
        });
        setDraggedEventIndex(index);
    };

    const handleEventDragEnd = () => {
        setDraggedEventIndex(null);
    };

    const handleAssetDragStart = (e: React.DragEvent, index: number) => {
        setDraggedAssetIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleAssetDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedAssetIndex === null || draggedAssetIndex === index) return;

        setAssetColumns(prev => {
            const updated = [...prev];
            const item = updated.splice(draggedAssetIndex, 1)[0];
            updated.splice(index, 0, item);
            return updated;
        });
        setDraggedAssetIndex(index);
    };

    const handleAssetDragEnd = () => {
        setDraggedAssetIndex(null);
    };

    // Load settings from backend/localStorage
    useEffect(() => {
        const fetchSettings = async () => {
            let activeSettings: any = null;

            try {
                const decoded = jwt_decode();
                const orgId = decoded?.organization_id ? Number(decoded.organization_id) : null;
                const clientId = level?.name === 'global' ? null : (level?.id ? Number(level.id) : null);

                const queryObj: any = { client_id: clientId };
                if (orgId !== null) {
                    queryObj.organization_id = orgId;
                }

                const response = await baseapi.get('/settings/', {
                    params: { query: JSON.stringify(queryObj) }
                });
                if (response.data && response.data.status) {
                    const resData = response.data.data;
                    if (Array.isArray(resData) && resData.length > 0) {
                        activeSettings = resData[0];
                    } else if (resData && !Array.isArray(resData)) {
                        activeSettings = resData;
                    }
                }
            } catch (err) {
                console.error("Failed to fetch settings from backend API", err);
            }

            // if (!activeSettings) {
            //     const stored = localStorage.getItem('organization_settings');
            //     if (stored) {
            //         try {
            //             activeSettings = JSON.parse(stored);
            //         } catch (e) {
            //             console.error("Failed to parse organization_settings from localStorage", e);
            //         }
            //     }
            // }

            if (activeSettings) {
                const tz = activeSettings.timezone || 'UTC';
                setTimezone(tz);

                const deleteDays = activeSettings.delete_assets_after_days !== undefined ? Number(activeSettings.delete_assets_after_days) : 0;
                setDeleteAssetsAfterDays(deleteDays);

                const bkLogoKey = activeSettings.logo || '';
                setLogoKey(bkLogoKey);

                if (bkLogoKey) {
                    if (bkLogoKey.startsWith('http') || bkLogoKey.startsWith('data:')) {
                        // setLogoUrl(bkLogoKey);
                        setLogo(bkLogoKey);
                    } else {
                        try {
                            const logoRes = await baseapi.get('/settings/logo', {
                                params: { key: bkLogoKey }
                            });
                            if (logoRes.data && logoRes.data.status && logoRes.data.data?.url) {
                                const fullUrl = logoRes.data.data.url;
                                // setLogoUrl(fullUrl);
                                setLogo(fullUrl);
                            }
                        } catch (err) {
                            console.error("Failed to fetch logo signed URL", err);
                            const fallbackUrl = `https://bucket-8vxvid.s3.ap-south-1.amazonaws.com/${bkLogoKey}`;
                            // setLogoUrl(fallbackUrl);
                            setLogo(fallbackUrl);
                        }
                    }
                } else {
                    // setLogoUrl(null);
                    setLogo(null);
                }

                let agentColsOrder = activeSettings.columnOrder;
                let agentSortKey = activeSettings.defaultSortKey;
                let agentSortDir = activeSettings.defaultSortDir;

                let eventColsOrder = activeSettings.eventColumnOrder;
                let eventSortKey = activeSettings.eventDefaultSortKey;
                let eventSortDir = activeSettings.eventDefaultSortDir;

                let assetColsOrder = activeSettings.assetColumnOrder;
                let assetSortKey = activeSettings.assetDefaultSortKey;
                let assetSortDir = activeSettings.assetDefaultSortDir;

                let tableSettingsArray = activeSettings.table_settings;
                if (typeof tableSettingsArray === 'string') {
                    try {
                        tableSettingsArray = JSON.parse(tableSettingsArray);
                    } catch (e) {
                        console.error("Failed to parse table_settings string", e);
                    }
                }

                if (tableSettingsArray && Array.isArray(tableSettingsArray)) {
                    const agentTS = tableSettingsArray.find((t: any) => t.table_name === 'agent' || t.key === 'agent');
                    if (agentTS) {
                        if (agentTS.column_order) agentColsOrder = agentTS.column_order;
                        if (agentTS.default_sort_key) agentSortKey = agentTS.default_sort_key;
                        if (agentTS.default_sort_dir) agentSortDir = agentTS.default_sort_dir;
                    }

                    const eventTS = tableSettingsArray.find((t: any) => t.table_name === 'event' || t.key === 'event');
                    if (eventTS) {
                        if (eventTS.column_order) eventColsOrder = eventTS.column_order;
                        if (eventTS.default_sort_key) eventSortKey = eventTS.default_sort_key;
                        if (eventTS.default_sort_dir) eventSortDir = eventTS.default_sort_dir;
                    }

                    const assetTS = tableSettingsArray.find((t: any) => t.table_name === 'asset' || t.key === 'asset');
                    if (assetTS) {
                        if (assetTS.column_order) assetColsOrder = assetTS.column_order;
                        if (assetTS.default_sort_key) assetSortKey = assetTS.default_sort_key;
                        if (assetTS.default_sort_dir) assetSortDir = assetTS.default_sort_dir;
                    }
                }

                if (agentSortKey) setDefaultSortKey(agentSortKey);
                if (agentSortDir) setDefaultSortDir(agentSortDir);
                if (agentColsOrder && Array.isArray(agentColsOrder)) {
                    const ordered = agentColsOrder
                        .map((key: string) => DEFAULT_COLUMNS.find(col => col.key === key))
                        .filter((col: any): col is typeof DEFAULT_COLUMNS[0] => !!col);

                    DEFAULT_COLUMNS.forEach(col => {
                        if (!ordered.some(o => o.key === col.key)) {
                            ordered.push(col);
                        }
                    });
                    setColumns(ordered);
                }

                if (eventSortKey) setEventDefaultSortKey(eventSortKey);
                if (eventSortDir) setEventDefaultSortDir(eventSortDir);
                if (eventColsOrder && Array.isArray(eventColsOrder)) {
                    const ordered = eventColsOrder
                        .map((key: string) => EVENT_DEFAULT_COLUMNS.find(col => col.key === key))
                        .filter((col: any): col is typeof EVENT_DEFAULT_COLUMNS[0] => !!col);

                    EVENT_DEFAULT_COLUMNS.forEach(col => {
                        if (!ordered.some(o => o.key === col.key)) {
                            ordered.push(col);
                        }
                    });
                    setEventColumns(ordered);
                }

                if (assetSortKey) setAssetDefaultSortKey(assetSortKey);
                if (assetSortDir) setAssetDefaultSortDir(assetSortDir);
                if (assetColsOrder && Array.isArray(assetColsOrder)) {
                    const ordered = assetColsOrder
                        .map((key: string) => ASSET_DEFAULT_COLUMNS.find(col => col.key === key))
                        .filter((col: any): col is typeof ASSET_DEFAULT_COLUMNS[0] => !!col);

                    ASSET_DEFAULT_COLUMNS.forEach(col => {
                        if (!ordered.some(o => o.key === col.key)) {
                            ordered.push(col);
                        }
                    });
                    setAssetColumns(ordered);
                }
            }
        };

        fetchSettings();
    }, [level]);

    // Handle Logo Upload via API
    const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            showError('Please upload an image file (PNG, JPG, SVG etc.)');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setIsLogoUploading(true);
        try {
            const response = await baseapi.post('/settings/upload-logo', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            if (response.data && response.data.status) {
                const data = response.data.data;
                // setLogoUrl(data.url);
                setLogoKey(data.key);
                setLogo(data.url);
                showSuccess('Logo uploaded successfully');
            } else {
                showError(response.data?.message || 'Failed to upload logo');
            }
        } catch (err: any) {
            console.error("Logo upload error", err);
            showError(err.response?.data?.message || 'Error uploading logo');
        } finally {
            setIsLogoUploading(false);
        }
    };

    const handleDeleteLogo = () => {
        setLogo(null);
        // setLogoUrl(null);
        setLogoKey('');
        showSuccess('Logo removed');
    };

    // Save all settings to API and localStorage
    const handleSave = async () => {
        if (isLogoUploading) {
            showError('Please wait for logo upload to finish before saving settings.');
            return;
        }
        setIsSaving(true);
        try {
            // const settingsPayload: OrgSettings = {
            //     logo: logoUrl || logo,
            //     timezone,
            //     columnOrder: columns.map(c => c.key),
            //     defaultSortKey,
            //     defaultSortDir,
            //     eventColumnOrder: eventColumns.map(c => c.key),
            //     eventDefaultSortKey,
            //     eventDefaultSortDir,
            //     assetColumnOrder: assetColumns.map(c => c.key),
            //     assetDefaultSortKey,
            //     assetDefaultSortDir
            // };
            // localStorage.setItem('organization_settings', JSON.stringify(settingsPayload));

            const tableSettings = [
                {
                    table_name: 'agent',
                    column_order: columns.map(c => c.key),
                    default_sort_key: defaultSortKey,
                    default_sort_dir: defaultSortDir
                },
                {
                    table_name: 'event',
                    column_order: eventColumns.map(c => c.key),
                    default_sort_key: eventDefaultSortKey,
                    default_sort_dir: eventDefaultSortDir
                },
                {
                    table_name: 'asset',
                    column_order: assetColumns.map(c => c.key),
                    default_sort_key: assetDefaultSortKey,
                    default_sort_dir: assetDefaultSortDir
                }
            ];

            const decoded = jwt_decode();
            const orgId = decoded?.organization_id ? Number(decoded.organization_id) : null;
            const clientId = level?.name === 'global' ? null : (level?.id ? Number(level.id) : null);

            const apiPayload = {
                client_id: clientId,
                organization_id: orgId,
                logo: logoKey || '',
                timezone: timezone,
                table_settings: tableSettings,
                delete_assets_after_days: deleteAssetsAfterDays
            };

            await baseapi.post('/settings/', apiPayload);
            showSuccess('Organization settings saved successfully');
        } catch (err: any) {
            console.error("Save settings error", err);
            showError(err.response?.data?.message || 'Failed to save organization settings');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="space-y-6  py-2">
            {/* Header Card */}
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                <div className="flex items-center gap-1.5">
                    <div className="bg-orange-600 p-1.5 rounded shadow-lg">
                        <Building className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h1 className="font-bold text-lg text-gray-200 leading-none">Organization</h1>
                    </div>
                </div>
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-bold text-[12px] px-5 py-2.5 rounded-xl shadow-lg shadow-orange-950/20 transition-all duration-200"
                >
                    {isSaving ? (
                        <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    ) : (
                        <Save size={16} />
                    )}
                    Save Settings
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Card: General Settings */}
                <div className="space-y-6">
                    <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-5 shadow-xl space-y-5">
                        <h3 className="text-sm font-bold text-orange-400 uppercase tracking-widest flex items-center gap-2 border-b border-slate-800/80 pb-2">
                            <Building size={14} /> General Settings
                        </h3>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {/* Logo Upload Section */}
                            <div className="space-y-3">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Organization Logo</label>
                                <div className="flex items-center gap-4">
                                    <div className="relative group w-16 h-16 rounded-xl bg-gray-900 border border-gray-700 overflow-hidden flex items-center justify-center flex-shrink-0">
                                        {isLogoUploading ? (
                                            <div className="w-6 h-6 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin" />
                                        ) : logo ? (
                                            <img src={logo} alt="Organization Logo" className="w-full h-full object-contain p-1" />
                                        ) : (
                                            <Building size={24} className="text-gray-600" />
                                        )}
                                    </div>
                                    <div className="space-y-1.5">
                                        <div className="flex gap-1.5">
                                            <label className="bg-gray-800 hover:bg-gray-700 text-gray-200 border border-gray-700 hover:border-gray-600 rounded-lg px-2.5 py-1 text-[10px] font-bold cursor-pointer transition-all duration-200">
                                                Choose File
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={handleLogoChange}
                                                />
                                            </label>
                                            {logo && (
                                                <button
                                                    onClick={handleDeleteLogo}
                                                    className="bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 border border-red-500/20 rounded-lg px-2.5 py-1 text-[10px] font-bold transition-all duration-200"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-[9px] text-gray-500 leading-normal">
                                            PNG, JPG, SVG supported.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Timezone Section */}
                            <div className="space-y-3 border-t sm:border-t-0 sm:border-l border-slate-800/80 pt-4 sm:pt-0 sm:pl-6">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Timezone</label>
                                <SingleSelectLocalSearchDropdown
                                    label=""
                                    icon={Clock}
                                    options={timezoneList}
                                    value={timezone}
                                    onChange={setTimezone}
                                    placeholder="Select Timezone"
                                    searchInputPlaceholder="Search timezones..."
                                />
                                <p className="text-[9px] text-gray-500 leading-normal">
                                    Default timezone for schedule planning.
                                </p>
                            </div>
                        </div>

                        {/* Asset Retention Policy */}
                        <div className="border-t border-slate-800/80 pt-5 space-y-4">
                            <h4 className="text-xs font-bold text-orange-400 uppercase tracking-widest flex items-center gap-2">
                                <Trash2 size={14} /> Asset Retention Policy
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="space-y-3">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Delete Assets After Days</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={deleteAssetsAfterDays}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setDeleteAssetsAfterDays(val === '' ? 0 : Math.max(0, Number(val)));
                                        }}
                                        className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-3 py-2.5 text-xs font-semibold focus:outline-none focus:border-orange-500"
                                        placeholder="0"
                                    />
                                    <p className="text-[9px] text-gray-500 leading-normal">
                                        Automatically delete assets inactive for this many days (0 to disable).
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Unified Column Customizer Card */}
                <div className="space-y-6">
                    <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl shadow-xl overflow-hidden divide-y divide-slate-800/60">
                        {/* Title Section */}
                        <div className="p-4 bg-slate-800/20">
                            <h3 className="text-xs font-bold text-orange-400 uppercase tracking-widest flex items-center gap-2">
                                <Sliders size={14} /> Column Layout & Sorting Customizers
                            </h3>
                        </div>

                        {/* Agent Table Accordion */}
                        <div className="transition-all duration-200">
                            <button
                                type="button"
                                onClick={() => setIsAgentOpen(!isAgentOpen)}
                                className="w-full flex items-center justify-between p-4 hover:bg-slate-700/10 transition-colors cursor-pointer text-left focus:outline-none"
                            >
                                <span className="text-[11px] font-bold text-gray-300 uppercase tracking-wider">
                                    Agent Table Columns
                                </span>
                                <ChevronDown className={`text-orange-500 w-4 h-4 transition-transform duration-300 ${isAgentOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {isAgentOpen && (
                                <div className="p-4 border-t border-slate-800/60 bg-slate-900/10 space-y-3">
                                    <p className="text-[11px] text-gray-400 leading-normal">
                                        Drag & drop rows to reorder columns. Select default sort key and direction.
                                    </p>

                                    <div className="border border-slate-800 rounded-xl overflow-hidden bg-gray-900/60 max-h-[220px] overflow-y-auto custom-scroll">
                                        <table className="w-full text-left text-[11px] border-collapse">
                                            <thead className="bg-slate-800/60 sticky top-0 z-10">
                                                <tr className="border-b border-slate-800 text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                                                    <th className="px-2.5 py-1.5 text-center w-12">Reorder</th>
                                                    <th className="px-2.5 py-1.5">Column Name</th>
                                                    <th className="px-2.5 py-1.5 text-center w-20">Sort Key</th>
                                                    <th className="px-2.5 py-1.5 text-center w-24">Direction</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-800">
                                                {columns.map((col, index) => {
                                                    const canSort = col.key !== 'actions';
                                                    const isDefaultSort = defaultSortKey === col.key;

                                                    return (
                                                        <tr
                                                            key={col.key}
                                                            draggable
                                                            onDragStart={(e) => handleDragStart(e, index)}
                                                            onDragOver={(e) => handleDragOver(e, index)}
                                                            onDragEnd={handleDragEnd}
                                                            className={`hover:bg-slate-800/20 text-gray-300 font-medium transition-all ${draggedIndex === index
                                                                ? 'opacity-40 bg-slate-800 border-2 border-dashed border-orange-500/50'
                                                                : ''
                                                                }`}
                                                        >
                                                            <td className="px-2 py-1 text-center cursor-grab active:cursor-grabbing">
                                                                <div className="flex items-center justify-center text-gray-500 hover:text-orange-500">
                                                                    <GripVertical size={14} />
                                                                </div>
                                                            </td>
                                                            <td className="px-2.5 py-1 font-semibold">{col.label}</td>
                                                            <td className="px-2.5 py-1 text-center">
                                                                {canSort ? (
                                                                    <input
                                                                        type="radio"
                                                                        name="defaultSortKey"
                                                                        checked={isDefaultSort}
                                                                        onChange={() => setDefaultSortKey(col.key)}
                                                                        className="w-3 h-3 accent-orange-600 cursor-pointer"
                                                                    />
                                                                ) : (
                                                                    <span className="text-gray-600">-</span>
                                                                )}
                                                            </td>
                                                            <td className="px-2.5 py-1 text-center">
                                                                {canSort && isDefaultSort ? (
                                                                    <select
                                                                        value={defaultSortDir}
                                                                        onChange={(e) => setDefaultSortDir(e.target.value as 'asc' | 'desc')}
                                                                        className="bg-gray-800 border border-slate-700 text-white rounded px-1.5 py-0.5 text-[10px] font-semibold focus:outline-none focus:border-orange-500 cursor-pointer"
                                                                    >
                                                                        <option value="asc">Ascending</option>
                                                                        <option value="desc">Descending</option>
                                                                    </select>
                                                                ) : (
                                                                    <span className="text-gray-600">-</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Event Table Accordion */}
                        <div className="transition-all duration-200">
                            <button
                                type="button"
                                onClick={() => setIsEventOpen(!isEventOpen)}
                                className="w-full flex items-center justify-between p-4 hover:bg-slate-700/10 transition-colors cursor-pointer text-left focus:outline-none"
                            >
                                <span className="text-[11px] font-bold text-gray-300 uppercase tracking-wider">
                                    Event Table Columns
                                </span>
                                <ChevronDown className={`text-orange-500 w-4 h-4 transition-transform duration-300 ${isEventOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {isEventOpen && (
                                <div className="p-4 border-t border-slate-800/60 bg-slate-900/10 space-y-3">
                                    <p className="text-[11px] text-gray-400 leading-normal">
                                        Drag & drop rows to reorder columns. Select default sort key and direction.
                                    </p>

                                    <div className="border border-slate-800 rounded-xl overflow-hidden bg-gray-900/60 max-h-[220px] overflow-y-auto custom-scroll">
                                        <table className="w-full text-left text-[11px] border-collapse">
                                            <thead className="bg-slate-800/60 sticky top-0 z-10">
                                                <tr className="border-b border-slate-800 text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                                                    <th className="px-2.5 py-1.5 text-center w-12">Reorder</th>
                                                    <th className="px-2.5 py-1.5">Column Name</th>
                                                    <th className="px-2.5 py-1.5 text-center w-20">Sort Key</th>
                                                    <th className="px-2.5 py-1.5 text-center w-24">Direction</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-800">
                                                {eventColumns.map((col, index) => {
                                                    const canSort = col.key !== 'actions' && col.key !== 'client_name';
                                                    const isDefaultSort = eventDefaultSortKey === col.key;

                                                    return (
                                                        <tr
                                                            key={col.key}
                                                            draggable
                                                            onDragStart={(e) => handleEventDragStart(e, index)}
                                                            onDragOver={(e) => handleEventDragOver(e, index)}
                                                            onDragEnd={handleEventDragEnd}
                                                            className={`hover:bg-slate-800/20 text-gray-300 font-medium transition-all ${draggedEventIndex === index
                                                                ? 'opacity-40 bg-slate-800 border-2 border-dashed border-orange-500/50'
                                                                : ''
                                                                }`}
                                                        >
                                                            <td className="px-2 py-1 text-center cursor-grab active:cursor-grabbing">
                                                                <div className="flex items-center justify-center text-gray-500 hover:text-orange-500">
                                                                    <GripVertical size={14} />
                                                                </div>
                                                            </td>
                                                            <td className="px-2.5 py-1 font-semibold">{col.label}</td>
                                                            <td className="px-2.5 py-1 text-center">
                                                                {canSort ? (
                                                                    <input
                                                                        type="radio"
                                                                        name="eventDefaultSortKey"
                                                                        checked={isDefaultSort}
                                                                        onChange={() => setEventDefaultSortKey(col.key)}
                                                                        className="w-3 h-3 accent-orange-600 cursor-pointer"
                                                                    />
                                                                ) : (
                                                                    <span className="text-gray-600">-</span>
                                                                )}
                                                            </td>
                                                            <td className="px-2.5 py-1 text-center">
                                                                {canSort && isDefaultSort ? (
                                                                    <select
                                                                        value={eventDefaultSortDir}
                                                                        onChange={(e) => setEventDefaultSortDir(e.target.value as 'asc' | 'desc')}
                                                                        className="bg-gray-800 border border-slate-700 text-white rounded px-1.5 py-0.5 text-[10px] font-semibold focus:outline-none focus:border-orange-500 cursor-pointer"
                                                                    >
                                                                        <option value="asc">Ascending</option>
                                                                        <option value="desc">Descending</option>
                                                                    </select>
                                                                ) : (
                                                                    <span className="text-gray-600">-</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Asset Table Accordion */}
                        <div className="transition-all duration-200">
                            <button
                                type="button"
                                onClick={() => setIsAssetOpen(!isAssetOpen)}
                                className="w-full flex items-center justify-between p-4 hover:bg-slate-700/10 transition-colors cursor-pointer text-left focus:outline-none"
                            >
                                <span className="text-[11px] font-bold text-gray-300 uppercase tracking-wider">
                                    Asset Table Columns
                                </span>
                                <ChevronDown className={`text-orange-500 w-4 h-4 transition-transform duration-300 ${isAssetOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {isAssetOpen && (
                                <div className="p-4 border-t border-slate-800/60 bg-slate-900/10 space-y-3">
                                    <p className="text-[11px] text-gray-400 leading-normal">
                                        Drag & drop rows to reorder columns. Select default sort key and direction.
                                    </p>

                                    <div className="border border-slate-800 rounded-xl overflow-hidden bg-gray-900/60 max-h-[220px] overflow-y-auto custom-scroll">
                                        <table className="w-full text-left text-[11px] border-collapse">
                                            <thead className="bg-slate-800/60 sticky top-0 z-10">
                                                <tr className="border-b border-slate-800 text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                                                    <th className="px-2.5 py-1.5 text-center w-12">Reorder</th>
                                                    <th className="px-2.5 py-1.5">Column Name</th>
                                                    <th className="px-2.5 py-1.5 text-center w-20">Sort Key</th>
                                                    <th className="px-2.5 py-1.5 text-center w-24">Direction</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-800">
                                                {assetColumns.map((col, index) => {
                                                    const canSort = !!col.sortKey;
                                                    const isDefaultSort = assetDefaultSortKey === col.sortKey;

                                                    return (
                                                        <tr
                                                            key={col.key}
                                                            draggable
                                                            onDragStart={(e) => handleAssetDragStart(e, index)}
                                                            onDragOver={(e) => handleAssetDragOver(e, index)}
                                                            onDragEnd={handleAssetDragEnd}
                                                            className={`hover:bg-slate-800/20 text-gray-300 font-medium transition-all ${draggedAssetIndex === index
                                                                ? 'opacity-40 bg-slate-800 border-2 border-dashed border-orange-500/50'
                                                                : ''
                                                                }`}
                                                        >
                                                            <td className="px-2 py-1 text-center cursor-grab active:cursor-grabbing">
                                                                <div className="flex items-center justify-center text-gray-500 hover:text-orange-500">
                                                                    <GripVertical size={14} />
                                                                </div>
                                                            </td>
                                                            <td className="px-2.5 py-1 font-semibold">{col.label}</td>
                                                            <td className="px-2.5 py-1 text-center">
                                                                {canSort ? (
                                                                    <input
                                                                        type="radio"
                                                                        name="assetDefaultSortKey"
                                                                        checked={isDefaultSort}
                                                                        onChange={() => setAssetDefaultSortKey(col.sortKey!)}
                                                                        className="w-3 h-3 accent-orange-600 cursor-pointer"
                                                                    />
                                                                ) : (
                                                                    <span className="text-gray-600">-</span>
                                                                )}
                                                            </td>
                                                            <td className="px-2.5 py-1 text-center">
                                                                {canSort && isDefaultSort ? (
                                                                    <select
                                                                        value={assetDefaultSortDir}
                                                                        onChange={(e) => setAssetDefaultSortDir(e.target.value as 'asc' | 'desc')}
                                                                        className="bg-gray-800 border border-slate-700 text-white rounded px-1.5 py-0.5 text-[10px] font-semibold focus:outline-none focus:border-orange-500 cursor-pointer"
                                                                    >
                                                                        <option value="asc">Ascending</option>
                                                                        <option value="desc">Descending</option>
                                                                    </select>
                                                                ) : (
                                                                    <span className="text-gray-600">-</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
